import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import toast from "react-hot-toast";

export default function AdminWebhooksPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await api.get("/admin/webhooks");
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load webhook logs");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const tableRows = useMemo(() => events.slice(0, 200), [events]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Webhook Logs</h1>
        <p className="mt-2 text-sm text-slate-400">
          Review incoming forwarded webhook events and troubleshoot webhook delivery problems.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Events Loaded</p>
          <p className="mt-3 text-3xl font-bold text-white">{tableRows.length}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Processed</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">
            {tableRows.filter((event) => event.status === "processed").length}
          </p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Failed</p>
          <p className="mt-3 text-3xl font-bold text-rose-300">
            {tableRows.filter((event) => event.status === "failed").length}
          </p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 md:p-6 backdrop-blur-xl">
        {loading && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-slate-300">
            Loading webhook logs...
          </div>
        )}

        {!loading && tableRows.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-slate-400">
            No webhook events found.
          </div>
        )}

        {!loading && tableRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-2 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2 font-semibold">Event ID</th>
                  <th className="px-4 py-2 font-semibold">Provider</th>
                  <th className="px-4 py-2 font-semibold">Event Type</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((event) => (
                  <tr key={event._id} className="bg-white/5 text-slate-200">
                    <td className="rounded-l-2xl px-4 py-4 text-slate-300">{event._id.slice(-8)}</td>
                    <td className="px-4 py-4 text-white capitalize">{event.provider}</td>
                    <td className="px-4 py-4 text-slate-300">{event.eventType}</td>
                    <td className="px-4 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        event.status === "processed"
                          ? "bg-emerald-500/15 text-emerald-300"
                          : event.status === "pending"
                          ? "bg-amber-500/15 text-amber-300"
                          : "bg-rose-500/15 text-rose-300"
                      }`}>
                        {event.status}
                      </span>
                    </td>
                    <td className="rounded-r-2xl px-4 py-4 text-slate-400">{new Date(event.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
